import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DeviceStatus } from "@/api/services/manageDevicesServices";
import {
  FilterPanel,
  FilterSection,
  FilterCheckbox,
  FilterEmptyNote,
} from "@/components/FilterPanel";

export interface DeviceFilterValues {
  statuses: string[];
  deviceTypes: string[];
  locations: string[];
}

export interface FilterOption {
  label: string;
  value: string;
}

export interface ManageDevicesFilterPanelProps {
  open: boolean;
  /** Currently applied filter values (used to seed the draft when opened). */
  value: DeviceFilterValues;
  /** Device-type options with display labels (e.g. { label: "Security Panel", value: "SecurityPanel" }). */
  deviceTypeOptions: FilterOption[];
  /** Location names fetched from the API. */
  locationOptions: string[];
  /** True while API-backed filter options are still loading. */
  isOptionsLoading?: boolean;
  onApply: (next: DeviceFilterValues) => void;
  onClose: () => void;
}

const STATUS_VALUES: DeviceStatus[] = ["online", "offline"];

const EMPTY: DeviceFilterValues = {
  statuses: [],
  deviceTypes: [],
  locations: [],
};

const toggle = (list: string[], value: string): string[] =>
  list.includes(value)
    ? list.filter((v) => v !== value)
    : [...list, value];

export const ManageDevicesFilterPanel = ({
  open,
  value,
  deviceTypeOptions,
  locationOptions,
  isOptionsLoading = false,
  onApply,
  onClose,
}: ManageDevicesFilterPanelProps) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<DeviceFilterValues>(value);
  const [expandedGroups, setExpandedGroups] = useState<Record<keyof DeviceFilterValues, boolean>>({
    statuses: true,
    deviceTypes: true,
    locations: true,
  });

  const statusOptions: FilterOption[] = STATUS_VALUES.map((v) => ({
    label: t(`manageDevices.statusOptions.${v}`),
    value: v,
  }));

  // Re-seed the draft from the applied values every time the panel opens so
  // that cancelling discards any unsaved edits.
  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const renderGroup = (
    title: string,
    options: FilterOption[],
    key: keyof DeviceFilterValues
  ) => {
    const isApiBackedGroup = key !== "statuses";
    if (isApiBackedGroup && !isOptionsLoading && options.length === 0) {
      return null;
    }

    return (
      <FilterSection
        title={title}
        count={draft[key].length}
        expanded={expandedGroups[key]}
        onToggle={() => setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }))}
      >
        {isOptionsLoading && isApiBackedGroup && options.length === 0 ? (
          <FilterEmptyNote>{t("manageDevices.loadingOptions")}</FilterEmptyNote>
        ) : options.length === 0 ? (
          <FilterEmptyNote>{t("manageDevices.noOptions")}</FilterEmptyNote>
        ) : (
          options.map((option) => (
            <FilterCheckbox
              key={option.value}
              label={option.label}
              checked={draft[key].includes(option.value)}
              onChange={() =>
                setDraft((prev) => ({
                  ...prev,
                  [key]: toggle(prev[key], option.value),
                }))
              }
            />
          ))
        )}
      </FilterSection>
    );
  };

  const labelFor = (options: FilterOption[], optionValue: string) =>
    options.find((option) => option.value === optionValue)?.label ?? optionValue;

  const removeChip = (group: keyof DeviceFilterValues, optionValue: string) =>
    setDraft((prev) => ({
      ...prev,
      [group]: prev[group].filter((v) => v !== optionValue),
    }));

  const chips = (
    [
      ["statuses", draft.statuses, (v: string) => labelFor(statusOptions, v)],
      ["deviceTypes", draft.deviceTypes, (v: string) => labelFor(deviceTypeOptions, v)],
      ["locations", draft.locations, (v: string) => v],
    ] as const
  ).flatMap(([group, values, toLabel]) =>
    values.map((v) => {
      const label = toLabel(v);
      return {
        key: `${group}-${v}`,
        label,
        removeLabel: t("manageDevices.removeFilter", { label }),
        onRemove: () => removeChip(group, v),
      };
    })
  );

  return (
    <FilterPanel
      isOpen={open}
      title={t("manageDevices.filterHeading")}
      ariaLabel={t("manageDevices.ariaLabels.filterDevices")}
      chips={chips}
      labels={{
        clearAll: t("manageDevices.filterClearAll"),
        close: t("manageDevices.ariaLabels.closeFilterPanel"),
        selected: t("manageDevices.filterSelected"),
        cancel: t("manageDevices.cancel"),
        apply: t("manageDevices.apply"),
      }}
      onClose={onClose}
      onApply={() => onApply(draft)}
      onClearAll={() => setDraft(EMPTY)}
    >
      {renderGroup(t("manageDevices.groups.status"), statusOptions, "statuses")}
      {renderGroup(t("manageDevices.groups.deviceType"), deviceTypeOptions, "deviceTypes")}
      {renderGroup(
        t("manageDevices.groups.location"),
        locationOptions.map((name) => ({ label: name, value: name })),
        "locations"
      )}
    </FilterPanel>
  );
};

export default ManageDevicesFilterPanel;
